/**
 * Fetches chat widget styles and company details from the proxy API
 * @param {string|null} companyId - The company ID to fetch styles for (can be null)
 * @returns {Promise<object>} The style object with colors, display settings, and company details
 */
export const fetchStyles = async (companyId) => {
  // If no company ID is provided, return default styles immediately
  if (!companyId) {
    return getDefaultStyles();
  }

  try {
    // Your Appwrite Function URL
    const proxyApiUrl = `https://680174d70eaff75f2a32.fra.appwrite.run/company-styles/${companyId}`;
    
    
    // Fetch from the proxy API
    try {
      const response = await fetch(proxyApiUrl);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      
      
      // Check if the API request was successful
      if (data.success === false) {
        console.warn(`API request failed: ${data.message}`);
        throw new Error(`API request failed: ${data.message}`);
      }
      
      // ✅ IMPORTANT: Log the actual textColor value being returned
      
      // 🆕 NEW: Parse announcements if they come as JSON strings
      if (data.styles && data.styles.announcements) {
        data.styles.announcements = parseAnnouncements(data.styles.announcements);      }
      
      // Return the styles object from the API response
      return data.styles;
    } catch (fetchError) {
      console.warn('Primary fetch failed, using default styles:', fetchError.message);
      throw fetchError; // Re-throw to fall back to defaults
    }
  } catch (error) {
    console.error("Error fetching widget styles:", error);
    
    // 🆕 NEW: Try to get styles directly from Appwrite as fallback
    try {
      const directStyles = await getStylesDirectFromAppwrite(companyId);
      if (directStyles) {
        return directStyles;
      }
    } catch (directError) {
      console.warn('Direct Appwrite fallback also failed:', directError.message);
    }
    
    // Return default styles on error
    return getDefaultStyles(companyId);
  }
};

// 🆕 NEW: Helper function to parse announcements (same as in proxy)
function parseAnnouncements(announcementsData) {
  if (!announcementsData) return [];
  
  try {
    if (Array.isArray(announcementsData)) {
      // Check if array contains strings (JSON) or objects
      if (announcementsData.length > 0 && typeof announcementsData[0] === 'string') {
        // Parse JSON strings back to objects
        return announcementsData.map(announcementStr => {
          try {
            return JSON.parse(announcementStr);
          } catch (error) {
            console.error('Error parsing announcement JSON:', error);
            return null;
          }
        }).filter(Boolean);
      } else {
        // Already objects, use directly
        return announcementsData;
      }
    } else if (typeof announcementsData === 'string') {
      // Try to parse as JSON array
      try {
        const parsed = JSON.parse(announcementsData);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Error parsing announcements string:', error);
        return [];
      }
    }
  } catch (error) {
    console.error('Error processing announcements:', error);
  }
  
  return [];
}

// 🆕 NEW: Direct Appwrite fallback function
async function getStylesDirectFromAppwrite(companyId) {
  try {
    // Import the fetchAnnouncements function
    const { fetchAnnouncements } = await import('../lib/appwrite');
    
    const announcementData = await fetchAnnouncements(companyId);
    
    if (announcementData && announcementData.announcements.length > 0) {
      
      // Return minimal styles with announcements
      const defaultStyles = getDefaultStyles(companyId);
      defaultStyles.announcementsEnabled = announcementData.enabled;
      defaultStyles.announcements = announcementData.announcements;
      
      return defaultStyles;
    }
  } catch (error) {
    console.error('Direct Appwrite fetch failed:', error);
  }
  
  return null;
}

/**
 * Returns the default styles configuration
 * @param {string|null} companyId - Optional company ID to include in defaults
 * @returns {object} Default styles object
 */
export const getDefaultStyles = (companyId = null) => {
  const defaultStyles = {
    companyID: companyId,
    bubbleColor: '#1A1A1A',
    bubbleHoverColor: '#1a1a1a',
    homeBgGradientTop: '#212529',
    homeBgGradientBottom: '#343a40',
    
    // Toggle switches for UI elements
    showTextSuggestions: true,
    showMoodBar: true,
    showCallback: true,
    showChatChannels: true,
    showContactForm: true,
    showBanner: true,
    showAvatars: true,
    showMeeting: true,
    
    // Color customization
    textColor: '#ffffff',
    buttonColor: null,
    
    // Company branding
    companyName: 'Chatly',
    companyLogo: null,
    welcomeText: 'Hey👋, wie geht es dir?',
    welcomeSubText: 'Wir sind hier, um dir bei allen Fragen zu helfen.',
    
    // Avatar customization
    customerAvatar: null,
    
    // Support topics
    supportTopic1: 'Ich habe eine Frage zu XYZ',
    supportTopic2: 'Ich will einen Termin buchen',
    supportTopic3: 'Ich habe ein Technisches Problem',
    supportTopic4: 'Wie funktioniert XY?',
    
    supportTopic1Target: 'ChatScreen',
    supportTopic2Target: 'TerminScreen',
    supportTopic3Target: 'ChatScreen',
    supportTopic4Target: 'FaqScreen',
    
    supportTopicsTitle: 'Quick Support',
    
    // Banner configuration
    bannerImage: 'https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?q=80&w=2070&auto=format&fit=crop',
    bannerTitle: 'Black Friday Sale!',
    bannerSubTitle: 'Sichere dir 30% Rabatt auf alle Produkte',
    bannerLink: 'https://example.com/sale',
    bannerButton: '',
    aspectRatio: 'rectangle',
    
    // Contact information
    instagramLink: 'yourcompany',
    whatsappNumber: 'yourphonenumber',
    emailAddress: 'contact@yourcompany.com',
    phoneNumber: '+49123456789',
    
    channelsSectionTitle: 'Du erreichst uns auch hier:',
    
     backgroundTexture: null,
    // Mood tracking
    moodRatings: [],
    moodRatingAverage: 0,
    moodRatingCount: 0,
    lastMoodRating: 0,
    
    // Announcements settings
    announcementsEnabled: false,
    announcements: []
  };
  
  
  return defaultStyles;
};